import React, { useCallback, useMemo } from "react";
import PropType from "prop-types";
import { Box } from "@material-ui/core";
import {
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  Typography,
  Link,
} from "@material-ui/core";
import ButtonWithSpinner from "../../ButtonWithSpinner/ButtonWIthSpinner";

const cancellingReasons = [
  "I don't understand how to use your product.",
  "It's too expensive.",
  "I found another option or product that I like better.",
  "I don't use it enough.",
  "Some features I need are missing.",
  "Bugs or Support Problems.",
  "Other reasons",
];

const CALENDLY_URL = "https://calendly.com/jasonalexis/ccbchimp";

const CancelPlan = (props) => {
  const {
    showModal,
    toggleModal,
    handleCancellationReasonChange,
    cancellationReason,
    loading,
    stopSync,
    handleStopSyncChange,
    onCustomCancellationReasonChange,
    customCalcellationReason,
    setShowFinalConfirmation,
    switchingPlatform,
    setSwitchingPlatform,
  } = props;

  const disabledCondition = useMemo(() => {
    if (loading || !cancellationReason) return true;
    if (cancellationReason === "Other reasons" && !customCalcellationReason)
      return true;
    if (
      cancellationReason ===
        "I found another option or product that I like better." &&
      !switchingPlatform
    )
      return true;
    return false;
  }, [
    cancellationReason,
    customCalcellationReason,
    loading,
    switchingPlatform,
  ]);

  const closeModalAndOpenIntercom = useCallback(() => {
    if (window?.Intercom) {
      window.Intercom("show");
    }
    toggleModal();
  }, [toggleModal]);

  const renderFollowUp = () => {
    if (!cancellationReason) return null;

    return (
      <Box
        mt={3}
        mb={2}
        p={2}
        border={1}
        borderColor="grey.300"
        borderRadius={4}
        bgcolor="#fafafa"
      >
        {(() => {
          switch (cancellationReason) {
            case "I don't understand how to use your product.":
              return (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Want a personalized setup?
                  </Typography>
                  <Link href={CALENDLY_URL} target="_blank" rel="noopener">
                    Book a 1-on-1 setup call
                  </Link>
                </>
              );
            case "It's too expensive.":
              return (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Want to keep your account for less?
                  </Typography>
                  <Typography variant="body2">
                    We’d love to offer you a 25% discount or a 1–3 month pause
                    on your subscription.
                  </Typography>
                  <Box mt={1}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={closeModalAndOpenIntercom}
                    >
                      <b>Contact Support</b>
                    </Link>
                  </Box>
                </>
              );
            case "I found another option or product that I like better.":
              return (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    What platform are you switching to?
                  </Typography>
                  <FormControl
                    className="w-100 mt-2"
                    size="small"
                    variant="outlined"
                  >
                    <Select
                      value={switchingPlatform || "Please Select Platform"}
                      onChange={(event) =>
                        setSwitchingPlatform(event.target.value)
                      }
                    >
                      <MenuItem value="Please Select Platform" disabled>
                        Please Select Platform
                      </MenuItem>
                      <MenuItem value="Planning Center">
                        Planning Center
                      </MenuItem>
                      <MenuItem value="Realm">Realm</MenuItem>
                      <MenuItem value="Pushpay">Pushpay</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </>
              );

            case "I don't use it enough.":
              return (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Want to pause your plan?
                  </Typography>
                  <Typography variant="body2">
                    You can pause your subscription and resume anytime — no need
                    to cancel.
                  </Typography>
                  <Box mt={1}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={closeModalAndOpenIntercom}
                    >
                      <b>Contact Support</b>
                    </Link>
                  </Box>
                </>
              );
            case "Some features I need are missing.":
              return (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Let’s talk through what’s missing
                  </Typography>
                  <Link href={CALENDLY_URL} target="_blank" rel="noopener">
                    Schedule a quick call
                  </Link>
                </>
              );
            case "Bugs or Support Problems.":
              return (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Schedule a call to fix the issue
                  </Typography>
                  <Link href={CALENDLY_URL} target="_blank" rel="noopener">
                    Book a support session
                  </Link>
                </>
              );
            case "Other reasons":
              return (
                <Typography variant="body2">
                  Please share your reason in the comment box below so we can
                  better understand.
                </Typography>
              );
            default:
              return null;
          }
        })()}
      </Box>
    );
  };

  return (
    <Dialog
      open={showModal}
      onClose={toggleModal}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title" className="pb-0">
        <strong>Cancel subscription</strong>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have not canceled your account yet. Please select an option below.
          <br /> What's the single biggest reason for you canceling?
        </DialogContentText>

        <FormControl className="w-100 mt-2">
          <InputLabel id="cancel-reason-label">
            Please select canceling reason
          </InputLabel>
          <Select
            value={cancellationReason}
            labelId="cancel-reason-label"
            id="cancel-reason-select"
            name="reason"
            onChange={(event) =>
              handleCancellationReasonChange(event.target.value)
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {cancellingReasons.map((item, index) => (
              <MenuItem key={index} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {renderFollowUp()}

        <DialogContentText className="mt-4" style={{ fontWeight: 600 }}>
          We are sorry to see you go. You're welcome back at any time. Any final
          comments for us?
        </DialogContentText>

        <TextField
          id="outlined-multiline-static"
          label="Comments"
          name="comment"
          multiline
          rows={4}
          variant="outlined"
          onChange={onCustomCancellationReasonChange}
          value={customCalcellationReason}
          fullWidth
          required={cancellationReason === "Other reasons"}
        />

        <div className="flexer-start mt-3">
          <Checkbox
            checked={stopSync}
            onChange={handleStopSyncChange}
            style={{ color: "#2db8e8" }}
            className="mb-1"
          />
          <DialogContentText className="mt-2">
            Disable Automatic Syncing Immediately{" "}
            <a
              href="https://support.ccbchimp.com/en/articles/2105551-cancel"
              target="__blank"
              className="link-text"
            >
              (?)
            </a>
          </DialogContentText>
        </div>
      </DialogContent>

      <DialogActions className="mb-2">
        <ButtonWithSpinner onClick={toggleModal} className="dark-gray-button">
          Nevermind
        </ButtonWithSpinner>
        <ButtonWithSpinner
          onClick={() => setShowFinalConfirmation(true)}
          className="bg-yellow"
          disabled={disabledCondition}
        >
          Cancel
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

CancelPlan.propTypes = {
  toggleModal: PropType.func,
  showModal: PropType.bool,
};

export default CancelPlan;
